import { useCallback, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ListSourceType } from "@/enum/list.enum";

import { useUpdateCustomEventMutation } from "@/pages/Private/redux/customEvent/customEvent.api";
import { useRestartListProcessingMutation } from "@/pages/Private/redux/list/listProcessing.api";

import { CreateCustomEvent } from "../../../CustomEvent/schema/customEvent";
import { List } from "../../schema/list";
import { getListConfigurationStatus } from "../../utils/useListConfigurationStatus";
import { isDetailsNeeded } from "../../utils/create-list-steps";

/** Where the Sources step leads, which depends on the source type. */
const nextStepPath = (list: List): string => {
	if (list.sourceType === ListSourceType.TRACKER) {
		return `/app/lists/create/signals/${list.id}`;
	}

	if (isDetailsNeeded(list)) {
		return list.sourceType === ListSourceType.PARALLEL_AI
			? `/app/lists/create/details/${list.id}`
			: `/app/lists/create/filter/${list.id}`;
	}

	return `/app/lists/create/copywriting/${list.id}`;
};

/**
 * Leaving the Sources step.
 *
 * For a scraping source this is also where the request is *confirmed*. A list that has already
 * finished will not process a newly delivered file on its own (it is `ONCE`), so confirming is the
 * moment to offer restarting it — otherwise the file stays mapped and idle (PREC-3091).
 */
export const useSourceStepSubmit = (list?: List) => {
	const navigate = useNavigate();

	const [updateCustomEvent] = useUpdateCustomEventMutation();
	const [restartListProcessing, { isLoading: isRestarting }] = useRestartListProcessingMutation();

	const [isRestartPromptOpen, setIsRestartPromptOpen] = useState<boolean>(false);

	const goToNextStep = useCallback(() => {
		if (list) {
			navigate(nextStepPath(list));
		}
	}, [list, navigate]);

	const submit = useCallback(async () => {
		if (!list) {
			return;
		}

		const config = getListConfigurationStatus(list);

		if (list.sourceType === ListSourceType.CUSTOM_EVENTS && config.scrapingNotConfirmed) {
			await updateCustomEvent({
				...(list.customEvents?.[0] as CreateCustomEvent),
				confirmed: true,
			}).unwrap();
		}

		if (list.requiresProcessingRestart) {
			setIsRestartPromptOpen(true);

			return;
		}

		goToNextStep();
	}, [list, updateCustomEvent, goToNextStep]);

	/** Restart, then carry on to the next step either way — Next must not become a dead end. */
	const confirmRestart = useCallback(async () => {
		if (!list) {
			return;
		}

		try {
			await restartListProcessing(list.id).unwrap();
		} catch (err) {
			console.error(err);
		} finally {
			setIsRestartPromptOpen(false);
			goToNextStep();
		}
	}, [list, restartListProcessing, goToNextStep]);

	const skipRestart = useCallback(() => {
		setIsRestartPromptOpen(false);
		goToNextStep();
	}, [goToNextStep]);

	return { submit, isRestartPromptOpen, isRestarting, confirmRestart, skipRestart };
};
